<script setup lang="ts">
/**
 * CreditBadge — AI 이용권 잔량 GNB 상시 칩(워크스페이스 셸 우측).
 *
 * 이용권은 정시 학과 해석(057)·수시 6장 카드(060)가 **공용**으로 쓰는 통합 풀 1개다 —
 * 한쪽에 몰아 쓰면 다른 쪽을 못 쓰는 구조라, 잔량을 상시 노출해 소진을 미리 알게 한다.
 *
 * - 데이터는 057 가용성 API(적립 lazy 동기화 지점) — 페이지 쿼리와 같은 키로 캐시를 공유해
 *   생성 차감 시(invalidateQueries) 칩도 함께 갱신된다.
 * - 쿼터 비활성(무제한 — creditBalance=null)이면 렌더하지 않는다(057 FR-018 표기 숨김 규약).
 * - 미로그인이면 조회 자체를 하지 않는다(401 노이즈 방지).
 * - title 은 규칙 문장(대학 1곳당 M회) — 잔량 0 이고 후기를 쓰면 적립될 무료 대학이 있으면 후기 안내로 바뀐다(073).
 *   M 은 서버 정책값이라 칩은 데이터가 온 뒤에만 렌더한다(폴백 재기입 금지).
 * - 시각 기준: publishing/s6/parts.jsx TopBar6 의 "AI 이용권 N회" 칩(잔량 0 이면 muted).
 */
import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';

import { fetchOpinionAvailability } from '#/api/analysis/jeongsiOpinion';
import { creditRule, REVIEW_GATE_COPY } from '#/data/analysis/uiCopy';
import { useAuthStore } from '#/stores/auth';

const auth = useAuthStore();

const availabilityQuery = useQuery({
  queryKey: ['analysis', 'opinion', 'availability'],
  queryFn: () => fetchOpinionAvailability(),
  enabled: computed(() => auth.isAuthenticated),
});

const availability = computed(() => availabilityQuery.data.value ?? null);
const balance = computed(() => availability.value?.creditBalance ?? null);
const show = computed(() => auth.isAuthenticated && availability.value?.quotaEnabled === true);

/** 후기 안내 조건(spec Q1) — 잔량 0 ∧ 후기를 쓰면 새로 적립될 무료 대학 있음. */
const title = computed(() => {
  const a = availability.value;
  if (!a) return '';
  const reviewGate = (a.creditBalance ?? 0) <= 0 && a.reviewRequired;
  return reviewGate
    ? REVIEW_GATE_COPY.badgeTitle(a.grantPerUniversity)
    : creditRule(a.grantPerUniversity);
});
</script>

<template>
  <span
    v-if="show"
    class="hidden items-center gap-[7px] whitespace-nowrap rounded-field border border-line px-[11px] py-[6px] text-[13px] font-bold text-brand lg:inline-flex"
    :title="title"
  >
    AI 이용권
    <b class="tabular-nums" :class="(balance ?? 0) > 0 ? 'text-accent-deep' : 'text-muted'">
      {{ balance ?? 0 }}회
    </b>
  </span>
</template>
